import { AppResourceType } from "src/pages/App/pages/Organization/pages/Apps/services/types";
import { ApiError, MoneyApi } from "go-core/api/types";
import { ImageApi, ImageLinkApi } from "go-security/services/Api/typesSecurity";
import { CustomFieldEntityApi } from "go-segment/services/types";
import { AddressApi, SearchSelectApi } from "../types";

export type AppProvider =
	| "GOPOS"
	| "GOPOS_V2"
	| "GASTROSUPPLIER"
	| "GASTROSUPPLIER_MERCHANT"
	| "DOCUMENT_TRANSFER"
	| "LOCATIONS"
	| "DOCUMENT_FILE_IMPORT";

export interface ContactApi {
	id: number;
	status: string;
	name: string;
	tax_id_no: string;
	contact_details?: ContactDetailsApi;
	address?: AddressApi;
	comment: string;
	custom_fields: CustomFieldEntityApi[];
}

export interface ContactApiSearchSelect extends SearchSelectApi {
	document_transfer?: boolean;
}

export interface ContactDetailsApi {
	email: string;
	phone_number: string;
}

export interface ProductCategoryApi {
	id: number;
	uid: string;
	name: string;
	status: string;
	updatedAt: string;
	custom_fields: CustomFieldEntityApi[];
}

export type MeasureType = "ITEM" | "KILOGRAM" | "LITER" | "PACKAGE";

export interface ProductApi {
	id: number;
	name: string;
	category: ProductCategoryApi;
	category_id: number;
	last_price_money: MoneyApi;
	cost_price_money: MoneyApi;
	cost_price_gross_money: MoneyApi;
	sale_price_net_money: MoneyApi;
	default_price_net_money?: MoneyApi;
	default_volume: number;
	measure_type: MeasureType;
	state: StockProductStateApi;
	image_link?: ImageLinkApi;
	tax_id: number;
	type: ProductType;
	open_linkeds: boolean;
	status: "ENABLED" | "DISABLED" | "DELETED";
	product_volumes: ProductVolumeApi[];
	custom_fields: CustomFieldEntityApi[];
	custom_ids: ProductCustomIdApi[];
	connections?: ProductConnectionApi[];
	critical_amount_level?: number;
	optimal_amount_level?: number;
	sale_price_money?: MoneyApi;
	tax: TaxApi;
	recipes?: ProductRecipeApi[];
	sku?: string;
	sale_price?: number;
	volumes: number[];
}

export interface ProductVolumeApi {
	volume: number | null;
	label: string | null;
}

export interface ProductItemApi {
	amount: number;
	product_id: number;
	product_name: string;
	state: StockProductStateApi;
	measure_type: MeasureType;
	unit_product_cost_money: MoneyApi;
}

export interface ProductCustomIdApi {
	id: number;
	value: string;
	type: string;
}

export interface ProductConnectionApi {
	id: number;
	package_uid: string;
	name: string;
	barcodes: ProductConnectionBarcodeApi[];
}

export interface ProductConnectionSearchSelectApi {
	id: number;
	label: string;
	value: string;
}

export interface ProductConnectionBarcodeApi {
	barcode: string;
	volume: number;
}

export interface ProductRecipeApi {
	valid_from: string;
	recipe_amount: number | null;
	components: ProductComponentApi[];
}

export interface ProductComponentApi {
	id: number;
	product_id: number;
	product_cost_money: MoneyApi;
	product_cost_gross_money: MoneyApi;
	product_name: string;
	recipe_amount: number;
	unit_product_cost_money: MoneyApi;
	measure_type: MeasureType;
	sub_product_type: ProductType;
	unit_product_cost_gross_money: MoneyApi;
}

export type ProductType = "BASIC" | "PACKAGE" | "COMPOSITE";

export interface ProductStateApi {
	volumes: number[];
	product_state: StockProductStateApi;
	product_id: number;
}

export interface BankAccountApi {
	name: string;
	number: string;
	main: boolean;
}

export interface OrderApi {
	id: number | string;
	ordered_at: string;
	created_at: string;
	document_number: string;
	channel: ChannelApi;
	channel_id: string;
	reference_number: string;
	contact?: ContactApi;
	contact_id: number;
	stock?: StockApi;
	stock_id: number;
	status: "UNLINKED" | "DRAFT" | "OPEN" | "PARTIALLY_SHIPPED" | "SHIPPED" | "REVOKED" | "VOID";
	quantity: number;
	quantity_shipped: number;
	quantity_returned: number;
	tags: TagApi[];
	total_price_net_money: MoneyApi;
	total_price_gross_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
	total_mac_cost_net_money: MoneyApi;
	total_tax_gross_money: MoneyApi;
	lock_status: "LOCKED" | "UNLOCKED";
	comment?: string;
	file_attachments: FileAttachmentApi[];
	items: OrderItemApi[];
	tax_items: TaxItemApi[];
	tax_gross: boolean;
	custom_fields: CustomFieldEntityApi[];
	external_stock_name?: string;
	linked_contact_id?: number;
	external_contact_name?: string;
	linked_contact?: LinkedApi;
	source?: OrderSourceApi;
	linked_stock?: LinkedApi;
	external_stock_id?: string;
	created_by?: string;
	linked_stock_id?: string;
	shippable: boolean;
}

export interface OrderItemApi {
	position: number;
	id?: number;
	external_product_id?: string;
	product?: ProductApi;
	volume: number;
	quantity: number;
	price_net_money: MoneyApi;
	price_gross_money: MoneyApi;
	unit_fifo_cost_net_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
	total_price_net_money: MoneyApi;
	total_price_gross_money: MoneyApi;
	tax: TaxApi;
	tax_id: number;
	product_id?: number;
	items: OrderItemProductApi[];
	external_product_name?: string;
	comment?: string;
	linked_product?: LinkedApi;
	last_price?: number;
	channel?: ChannelApi;
	quantity_shipped?: number;
}

export interface OrderItemProductApi {
	id: number;
	product: ProductApi;
	quantity: number;
	unit_fifo_cost_net_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
}

export interface OrderSearchSelectApi extends SearchSelectApi {
	order_at: string;
}

export interface OrderShipApi {
	id: number | null;
	shipped_at: string;
	reference_number?: string;
	comment?: string;
	items: OrderShipItemApi[];
	number: string | null;
}

export interface OrderShipItemApi {
	item_id: number;
	quantity: number;
	quantity_shipped: number;
	quantity_to_ship: string;
	product: ProductApi;
	comment?: string;
}

export interface ChannelApi {
	name: string;
}

export interface CompanyApi {
	company_name: string;
	tax_id_no: string;
	bank_accounts: BankAccountApi[];
	address: AddressApi;
	business_register_no: string;
	country_code: string;
	currency: string;
	district: string;
	community: string;
	post_office: string;
	tax_office_code: string;
	voivodeship: string;
}

export interface BankAccountApi {
	name: string;
	number: string;
	main: boolean;
}

export interface StockAdjustmentApi {
	id: number;
	type: StockAdjustmentType;
	total_price_net_money: MoneyApi;
	total_mac_cost_net_money?: MoneyApi;
	total_fifo_cost_net_money?: MoneyApi;
	tags?: TagApi[];
	stock_id: number;
	contact_id: number;
	contact: ContactApi;
	stock: StockApi;
	status: StockAdjustmentStatus;
	reference_number: string;
	quantity: number;
	adjusted_at: string;
	created_at: string;
	comment: string;
	custom_fields: CustomFieldEntityApi[];
	document_number: string;
	items: StockAdjustmentItemApi[];
	file_attachments: FileAttachmentApi[];
}

export type StockAdjustmentStatus = "OPEN" | "DRAFT" | "CLOSED" | "VOID";

export interface StockAdjustmentItemApi {
	id?: number;
	product_id: number;
	product: ProductApi;
	volume: number;
	quantity: number;
	price_net_money: MoneyApi;
	total_price_net_money: MoneyApi;
	unit_mac_cost_net_money?: MoneyApi;
	total_mac_cost_net_money?: MoneyApi;
	unit_fifo_cost_net_money?: MoneyApi;
	total_fifo_cost_net_money?: MoneyApi;
	type: StockAdjustmentItemType;
	position: number;
	comment?: string;
}

export type StockAdjustmentType = "INCOME" | "EXPENSE" | "MIXED";
export type StockAdjustmentItemType = "INCOME" | "EXPENSE";

export interface StockProductStateApi {
	in_stock_amount: number;
	incoming_amount: number;
	available_amount: number;
	commited_amount: number;
}

export enum PurchaseStatus {
	"UNLINKED" = "UNLINKED",
	"DRAFT" = "DRAFT",
	"OPEN" = "OPEN",
	"PARTIALLY_RECEIVED" = "PARTIALLY_RECEIVED",
	"RECEIVED" = "RECEIVED",
	"VOID" = "VOID",
	"REVOKED" = "REVOKED",
}

export interface OrderSourceApi {
	link?: string;
	number: string;
	app_name: string;
	app_id: number;
	app_provider: AppProvider;
}

export interface PurchaseApi {
	id: number | string;
	purchased_at: string;
	status: PurchaseStatus;
	total_price_net_money: MoneyApi;
	contact?: ContactApi;
	stock?: StockApi;
	currency: string;
	comment: string;
	items: PurchaseItemApi[];
	custom_fields: CustomFieldEntityApi[];
	contact_id: number;
	tax_gross: boolean;
	stock_id: number;
	tags: TagApi[];
	file_attachments: FileAttachmentApi[];
	document_number: string;
	reference_number: string;
	created_at: string;
	channel?: ChannelApi;
	tax_items: TaxItemApi[];
	total_price_gross_money: MoneyApi;
	total_tax_price_money: MoneyApi;
	external_provider?: string;
	external_name?: string;
	lock_status: "LOCKED" | "UNLOCKED";
	quantity: number;
	quantity_received: number;
	shippable: boolean;
	total_fifo_cost_net_money: MoneyApi;
	external_contact_name?: string;
	linked_contact?: LinkedApi;
	external_stock_name?: string;
	linked_contact_id?: number;
	external_contact_id?: string;
	exchange_rate: number;
	source?: OrderSourceApi;
	created_by?: string;
	linked_stock_id?: number;
	channel_id?: number;
	linked_stock?: LinkedApi;
	total_tax_gross_money: MoneyApi;
}

export interface PurchaseItemApi {
	id?: number;
	product_id: number;
	tax_id: number;
	product: ProductApi;
	external_product_name?: string;
	external_product_id?: string;
	volume: number;
	quantity: number;
	quantity_received: number;
	price_net_money: MoneyApi;
	price_gross_money: MoneyApi;
	total_price_net_money: MoneyApi;
	total_price_gross_money: MoneyApi;
	default_price_net_money: MoneyApi;
	tax: TaxApi;
	position: number;
	comment: string;
	last_price?: number;
	linked_product?: LinkedApi;
	discounted: boolean;
}

export interface PurchaseReceiveApi {
	id: number;
	items: PurchaseReceiveItemApi[];
	reference_number: string;
	number: string;
	received_at: string;
}

export interface PurchaseReceiveItemApi {
	quantity: number;
	product_name: string;
	comment?: string;
}

export interface PurchaseCorrectionApi {
	id: number;
	corrected_at: string;
	number: string;
	status: PurchaseCorrectionStatus;
	reference_number?: string;
	custom_fields: CustomFieldEntityApi[];
	items: PurchaseCorrectionItemApi[];
	total_price_gross_money: MoneyApi;
	total_price_net_money: MoneyApi;
	reason?: string;
}

export interface PurchaseCorrectionItemApi {
	id: number | null;
	item_id?: number;
	price_net_money: MoneyApi | null;
	price_gross_money: MoneyApi | null;
	total_price_net_money: MoneyApi | null;
	total_price_gross_money: MoneyApi | null;
	volume: number | null;
	quantity: number | null;
	item_quantity?: number;
	product: ProductApi | null;
	comment?: string;
	tax: TaxApi | null;
	tax_id: number | null;
	product_id: number | null;
}

export enum PurchaseCorrectionStatus {
	CLOSED = "CLOSED",
	OPEN = "OPEN",
	VOID = "VOID",
}

export interface TaxItemApi {
	tax_id: number;
	name: string;
	tax_amount: number;
	price_net: MoneyApi;
	price_gross: MoneyApi;
	price_tax: MoneyApi;
}

export interface StockApi {
	id: number;
	name: string;
	status: "ENABLED" | "DISABLED" | "DELETED";
}

export interface TaxApi {
	id: number;
	name: string;
	code: string;
	amount: number;
}

export interface FileAttachmentApi {
	id: string;
	name: string;
	content: string;
	url: string;
	created_at: string;
}

export interface ChannelApi {
	id: number;
	provider: string;
	name: string;
}

export interface TagApi {
	id: number;
	name: string;
	slug: string;
}

export interface InvoiceDetailsApi {
	document_numeration_type: "NUMBER_YEAR" | "NUMBER_MONTH_YEAR";
	invoice_place: string;
	issuer_name: string;
	prefix: string;
	suffix: string;
	logo_link?: ImageLinkApi;
	logo?: ImageApi;
}

export interface WarehouseSettingsApi {
	stock_business_type: StockBusinessType;
	stocktaking_constraint_type: StockConstraintType;
	states_constraint_type: StockConstraintType;
	document_numbers: WarehouseDocumentNumberApi;
	archive_older_than_days: number;
	disable_duplicate_tax_id_no: boolean;
	strict_validate_draft_documents: boolean;
	auto_connect_product_packages: boolean;
}

export interface WarehouseDocumentNumberApi {
	purchase_order_prefix: string;
	purchase_receive_prefix: string;
	purchase_correction_prefix: string;
	sales_order_prefix: string;
	sales_return_prefix: string;
	sales_ship_prefix: string;
	transfer_order_prefix: string;
	stock_adjustment_prefix: string;
	bundle_prefix: string;
	stocktaking_prefix: string;
}

export type StockBusinessType = "BASIC" | "GASTRO" | "WHOLESALE";
export type StockConstraintType = "STRICT" | "WARNING" | "LOOSE";

export enum TransferStatus {
	DRAFT = "DRAFT",
	OPEN = "OPEN",
	RECEIVED = "RECEIVED",
	VOID = "VOID",
}

export interface TransferApi {
	id: number;
	transferred_at: string;
	number: number;
	status: TransferStatus;
	total_price_net_money: MoneyApi;
	source_stock: StockApi;
	destination_stock: StockApi;
	tags: TagApi[];
	file_attachments: FileAttachmentApi[];
	reference_number: string;
	items: TransferItemApi[];
	custom_fields: CustomFieldEntityApi[];
	source_stock_id: number;
	destination_stock_id: number;
	document_number: string;
	created_at: string;
	comment?: string;
	total_mac_cost_net_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
	created_by?: string;
}

export interface TransferItemApi {
	id?: number;
	position: number;
	product: ProductApi;
	product_id: number;
	volume: number;
	quantity: number;
	unit_fifo_cost_net_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
	unit_mac_cost_net_money: MoneyApi;
	total_mac_cost_net_money: MoneyApi;
	comment?: string;
	items: TransferItemProductApi[];
}

export interface TransferItemProductApi {
	id: number;
	product: ProductApi;
	quantity: number;
	unit_fifo_cost_net_money?: MoneyApi;
	total_fifo_cost_net_money?: MoneyApi;
	unit_mac_cost_net_money?: MoneyApi;
	total_mac_cost_net_money?: MoneyApi;
}

export type StocktakingStatus = "DRAFT" | "OPEN" | "CLOSED" | "VOID";

export interface StocktakingApi {
	id: number;
	stock: StockApi;
	document_number: number;
	stocktaking_status: StocktakingStatus;
	type: "AVAILABILITY" | "VALUE";
	tags: TagApi[];
	created_at: string;
	stocktaking_at: string;
	updated_at: string;
	comment?: string;
	custom_fields: CustomFieldEntityApi[];
	created_by?: string;
}

export interface LinkedApi {
	id: number;
	imported_at: string;
	external_name: string;
	resource_type: LinkedResourceType;
	integration_channel: Record<string, any>;
	resource_id: number;
	resource_name: string;
	resource: Record<string, any>;
	sku?: string;
	skipped: boolean;
	linked_stock_id?: string;
}

export interface LinkEntityApi extends LinkedApi {
	tax?: TaxApi;
}

export type LinkedResourceType = "PRODUCT" | "STOCK" | "CONTACT" | "PRODUCT_CATEGORY";

export interface LinkEntityFormProps {
	resource_id: string;
	volume?: number;
	item_name?: string;
}

export interface CreateAndCloseLinkedApi {
	name?: string;
	sku?: string;
	measure_type?: MeasureType;
	category_id?: number;
	tax_id?: number;
	recipe_amount?: number;
	components?: ProductComponentApi[];
	type?: ProductType;
	item_name?: string;
}

export interface StocktakingSummaryApi {
	total_amount_after_money: MoneyApi;
	total_amount_plus_money: MoneyApi;
	total_amount_minus_money: MoneyApi;
}

export interface StocktakingProductStateApi {
	product: ProductApi;
	amount_initial: number;
	amount_actual: number;
	amount_consumption: number;
	price_initial_net: MoneyApi;
	price_actual_net: MoneyApi;
	price_last_outflows_net: MoneyApi;
	last_price: MoneyApi;
	price_diff_net: MoneyApi;
	sold_amount: number;
	sold_diff_percent: number;
}

export interface StocktakingProductStateSummaryApi {
	data: StocktakingProductStateApi[];
	summary: SummaryApi;
}

export interface SummaryApi {
	amount_initial: number;
	amount_actual: number;
	amount_consumption: number;
	price_initial_net: MoneyApi;
	price_actual_net: MoneyApi;
	price_last_outflows_net: MoneyApi;
	price_diff_net: MoneyApi;
	sold_amount: number;
	sold_diff_percent: number;
}

export interface StockStateApi {
	product: ProductApi;
	state: StateApi[];
	summary: StockProductStateApi;
}

export interface StateApi {
	product_state?: StockProductStateApi;
	stock: StockApi;
}

export interface StocktakingDraftApi {
	id: number;
	name: string;
	type: StocktakingDraftType;
	states: StocktakingDraftStateApi[];
}

export enum StocktakingDraftType {
	MOBILE = "MOBILE",
	SCALES = "SCALES",
	DEFAULT = "DEFAULT",
}

export interface StocktakingDraftStateApi {
	product_id: number;
	amount?: number;
	id: number;
	product: ProductApi;
	volume?: number;
	value?: MoneyApi;
}

export interface StocktakingDraftSummaryStateApi {
	product_id: number;
	product_category: string;
	product_name: string;
	amount: number;
	measure_type: MeasureType;
	items: StocktakingDraftSummaryStateItemApi[];
	value?: MoneyApi;
	sku?: string;
}

export interface StocktakingDraftSummaryStateItemApi {
	volume: number;
	amount: number;
	stocktaking_draft_name: string;
	value?: MoneyApi;
}

export interface AutofillStocktakingDraftApi {
	volume_one: boolean;
	only_active: boolean;
	use_actual_states: boolean;
	categories: string[];
}

export interface TransactionApi {
	transaction_at: string;
	document_id: number;
	document_number: string;
	product: ProductApi;
	stock: StockApi;
	document_type: string;
	amount: number;
	open_amount: number;
	unit_fifo_price_money: MoneyApi;
	total_fifo_price_money: MoneyApi;
	stock_transaction_layer_items: ProductOutFIFOApi[];
}

export interface ProductOutFIFOApi {
	total_fifo_price_money: MoneyApi;
	transaction_at: string;
	document_type: string;
	amount: number;
}

export interface StateReportAggregateApi {
	state: number;
	total_fifo_price_money: MoneyApi;
	total_mac_price_money: MoneyApi;
	income_amount: number;
	income_price_money: MoneyApi;
	expense_amount: number;
	expense_mac_price_money: MoneyApi;
	expense_fifo_price_money: MoneyApi;
}

export interface StateReportSummaryApi {
	summary: StateReportAggregateApi;
}

export interface StateReportAggregate {
	aggregate: StateReportSummaryApi;
}

export interface ReportStateFieldsApi {
	aggregate: StateReportSummaryApi;
	compare_aggregate: StateReportSummaryApi;
	group_by_type: string;
	sub_report: StateReportAggregate[];
}

export interface ReportStateApi {
	reports: ReportStateFieldsApi[];
}

export interface CustomFieldSearchSelectApi {
	id: string;
	slug: string;
	opts: {
		id: number;
		value: string;
	}[];
	type: string;
	label: string;
}

export interface ApplicationTemplateApi {
	provider: string;
	short_description: string;
	long_description: string;
	help_description: string;
	owner: string;
	owner_link: string;
	logo_link: string;
	documentation_link: string;
	rules_link: string;
	privacy_policy_link: string;
	tags?: string[];
}

export interface ApplicationApi {
	status: string;
	id: number;
	provider: string;
	name: string;
	settings: Record<string, any>;
	link: string;
	last_export_menu: string;
	last_activity: string;
	inactive_since: string;
	errors: ApplicationError[];
	dangers: ApplicationError[];
}

export interface ApplicationUpdateApi {
	id: number;
	name: string;
	settings: Record<string, any>;
}

export interface ApplicationError extends ApiError {
	app_link_id: number;
}

export interface AppLinkApi {
	id: number;
	external_name: string;
	external_id: string;
	reference_id: string;
	skipped: boolean;
	resource_id: number;
	resource_type: AppResourceType;
	updated_at: string;
	created_at: string;
	imported_at: string;
	exported_at: string;
	used: boolean;
	status: string;
	parent_resource_id?: number;
	resource_name: string;
	app: ApplicationApi;
	resource?: Record<string, any>;
	location?: boolean;
	label: string;
	link: string;
	provider: AppResourceType;
}

export interface ApplicationServiceApi {
	id: string;
	name: string;
}

export interface PointOfSaleApi {
	id: number;
	status: string;
	name: string;
	default_direction: DirectionApi | undefined;
	default_direction_id: number;
	rooms: RoomApi[];
}

export interface DirectionApi {
	id: number;
	name: string;
	status: string;
	devices: DeviceApi[];
}

export interface RoomApi {
	id: number;
	name: string;
	status: string;
	number_of_seats: number;
	tables: RoomTableApi[];
	elements: RoomElementApi[];
	background_link?: ImageLinkApi;
	background?: ImageApi;
	color?: string;
	position: string;
	theme: string;
}

export interface DeviceApi {
	id: number;
	name: string;
	type: string;
	status: string;
	parameters: DeviceParameterApi[];
	terminal: DeviceTerminalApi;
	fiscal_receipt_printer_terminals: DeviceTerminalApi[];
	receipt_printer_terminals: DeviceTerminalApi[];
	payment_terminal_terminals: DeviceTerminalApi[];
	weight_terminals: DeviceTerminalApi[];
	directions: DeviceDirectionApi[];
	interface: string;
	terminal_uid: string;
}

export interface RoomTableApi {
	id?: number;
	name: string;
	type: RoomTypeApi;
	color: string;
	number: number;
	number_of_seats: number;
	position_top: number;
	position_left: number;
	height: number;
	width: number;
	status: string;
}

enum RoomElementType {
	RECTANGLE = "RECTANGLE",
	TOILET = "TOILET",
}

export interface RoomElementApi {
	id?: number;
	type: RoomElementType;
	name: string;
	color: string;
	position_top: number;
	position_left: number;
	height: number;
	width: number;
}

export interface DeviceParameterApi {
	name: string;
	value: string;
}

export interface DeviceTerminalApi {
	id: number;
	name: string;
}

export interface DeviceDirectionApi {
	id: number;
	name: string;
	status: string;
}

export enum RoomTypeApi {
	ROUND = "ROUND",
	SQUARE = "SQUARE",
	RECTANGLE = "RECTANGLE",
	HOKER = "HOKER",
}

export interface AppConfigApi {
	provider: string;
	name: string;
	description: string;
}

export interface AppStateApi {
	app: ApplicationApi;
	config?: Record<string, any>;
	showPointsOfSale?: boolean;
}

export interface JpkExportBodyApi {
	build_nr: string;
	business_register_no: string;
	city: string;
	community: string;
	country_code: string;
	date_range: string;
	default_currency_code: string;
	district: string;
	flat_nr: string;
	full_name: string;
	stock_id: string;
	street: string;
	tax_id_no: string;
	tax_office_code: string;
	post_office: string;
	voivodeship: string;
	zip_code: string;
	with_additional_data: boolean;
	stock: SearchSelectApi;
	file_type: JpkExportFileType;
}

export enum JpkExportFileType {
	JPK_MAG = "JPK_MAG",
	JPK_FA = "JPK_FA",
}

export type ProductPurchaseApi = {
	order_id: number;
	created_at: string;
	order_at: string;
	status: "UNLINKED" | "DRAFT" | "OPEN" | "PARTIALLY_RECEIVED" | "RECEIVED" | "REVOKED" | "VOID";
	document_number: string;
	reference_number: string;
	stock: StockApi;
	destination_stock: StockApi;
	contact?: ContactApi;
	amount: number;
	volume: number;
	comment?: string;
	volume_price_net_money: MoneyApi;
	volume_price_gross_money: MoneyApi;
	unit_price_net_money: MoneyApi;
	unit_price_gross_money: MoneyApi;
	total_price_net_money: MoneyApi;
	total_price_gross_money: MoneyApi;
	total_fifo_cost_net_money: MoneyApi;
};

export type ProductSalesApi = ProductPurchaseApi & {
	status: "UNLINKED" | "DRAFT" | "OPEN" | "PARTIALLY_SHIPPED" | "SHIPPED" | "REVOKED" | "VOID";
};

export type ProductTransferApi = ProductPurchaseApi & {
	status: "DRAFT" | "OPEN" | "RECEIVED" | "VOID";
};

export type ProductStockAdjustmentApi = ProductPurchaseApi & {
	status: StockAdjustmentStatus;
};

export interface AlertsApi {
	open_products_count?: number;
	products_below_critical_amount?: number;
	unlinked_products_count?: number;
	unlinked_purchase_order_products_count?: number;
	unlinked_sales_order_count?: number;
	unlinked_stocks_count?: number;
	below_zero_stocks?: AlertStock[];
	error_channels?: Pick<Array<any>, "length">;
	last_calculate_job_at_minutes_ago?: boolean;
}

export interface AlertStock {
	stock_id: number;
	stock_name?: string;
	below_zero_count?: number;
	products: AlertProduct[];
}

export interface AlertProduct {
	id: number;
	measure_type?: MeasureType;
	name?: string;
	state?: Partial<StockProductStateApi>;
}

type DiagnosticDocumentType =
	| "PURCHASE_ORDER"
	| "SALES_ORDER"
	| "TRANSFER_ORDER"
	| "STOCKTAKING"
	| "STOCK_ADJUSTMENT"
	| "ARCHIVE"
	| "BUNDLE";

export interface ErrorDiagnosticProduct {
	message: string;
	type:
		| "LAYER_OPEN_AMOUNT_GREATER"
		| "LAYER_OPEN_FIFO_PRICE_DIFF_THAN_CALCULATE"
		| "LAYER_USED_AMOUNT_DIFF_THAN_SUM_ITEMS"
		| "LAYER_USED_PRICE_DIFF_THAN_SUM_ITEMS"
		| "LAYER_MAIN_SOURCE_TRANSACTION_DIFF_AMOUNT"
		| "LAYER_MAIN_SOURCE_TRANSACTION_DIFF_PRICE"
		| "LAYER_FIRST_INCOME_TRANSACTION_NOT_EXIST"
		| "LAYER_MAIN_COST_TRANSACTION_NOT_EXIST_IN_COSTS"
		| "LAYER_FIRST_INCOME_TRANSACTION_MULTIPLE"
		| "LAYER_PRICE_PER_ONE_DIFF_FIRST_TRANSACTION"
		| "LAYER_AMOUNT_GREATER_THAN_FIRST_TRANSACTION"
		| "LAYER_SOURCES_PRICE_PER_ONE_DIFF_FIRST_TRANSACTION";
}

export interface ShouldAmountDiagnosticApi {
	amount: number;
	should_amount: number;
	diff_amount: number;
}

export interface LayerSourceDiagnosticApi {
	transaction_id: number;
	document_id: number;
	date: string;
	document_type: DiagnosticDocumentType;
	price_per_one: ShouldAmountDiagnosticApi;
}

export interface LayerDiagnosticProduct {
	layer_id: string;
	date: string;
	document_type: DiagnosticDocumentType;
	sources: LayerDiagnosticProduct[];
	open_amount: number;
	amount: number;
	price_per_one: ShouldAmountDiagnosticApi;
	should_open_fifo_price: ShouldAmountDiagnosticApi;
	should_used_amount: ShouldAmountDiagnosticApi;
	should_used_price: ShouldAmountDiagnosticApi;
	diff_transaction_price: ShouldAmountDiagnosticApi;
	errors: ErrorDiagnosticProduct[];
}

export interface DiagnosticProductApi {
	name: string;
	stock_id: number;
	products: LayerDiagnosticProduct[];
}

export interface CostItemsDiagnosticProduct {
	date: string;
	layer_id: number;
	transaction_id: number;
	document_type: DiagnosticDocumentType;
	document_number: string;
	document_id: number;
	amount: number;
	price: number;
	costs: CostItemsDiagnosticProduct[];
}

export interface LayerItemDiagnosticProductTransactionApi {
	transaction_id: number;
	date: string;
	amount: number;
	price: number;
	document_type: DiagnosticDocumentType;
	document_number: string;
	document_id: number;
}

export interface LayerDiagnosticProductTransaction {
	layer_id: number;
	source_id: number;
	document_type: DiagnosticDocumentType;
	document_number: string;
	document_id: number;
	amount: string;
	price: string;
	open_amount: string;
	open_price: string;
	items: LayerItemDiagnosticProductTransactionApi[];
}

export interface DiagnosticProductTransaction {
	transaction_id: number;
	date: string;
	document_type: DiagnosticDocumentType;
	document_number: string;
	document_id: number;
	amount: number;
	price: number;
	sum_amount: number;
	sum_price: number;
	layer: LayerDiagnosticProductTransaction;
	costs: CostItemsDiagnosticProduct[];
}

export interface DiagnosticProductTransactionResponse {
	stock_id: number;
	name: string;
	products: DiagnosticProductTransaction[];
}

export interface ErrorDiagnostic2ReportApi {
	level: string;
	message: string;
	type:
		| "OPEN_AMOUNT_GREATER_THAN_AMOUNT"
		| "OPEN_AMOUNT_LESS_THEN_ZERO"
		| "OPEN_AMOUNT_SAME_VALUE_DIFF"
		| "OPEN_VALUE_DIFF_CALC"
		| "ITEMS_OPEN_AMOUNT_DIFF"
		| "ITEMS_OPEN_VALUE_DIFF"
		| "OPEN_VALUE_ERROR"
		| "OPEN_AMOUNT_DIFF_ACTUAL_AMOUNT"
		| "OPEN_VALUE_DIFF_ACTUAL_VALUE"
		| "DOCUMENT_AMOUNT_DIFF";
}

export interface InfoAmountAndValueDiagnostic2ReportApi {
	product_id: number;
	stock_id: number;
	product_name: string;
	stock_name: string;
	open_amount: number;
	open_value: number;
	current_amount: number;
	current_value: number;
	archive_value: number;
	expanse_open_amount: number;
	diff_open_amount: number;
	diff_open_value: number;
	so_amount: number;
	to_amount: number;
	po_amount: number;
}

export interface DocumentAmountDiagnostic2ReportApi {
	product_id: number;
	stock_id: number;
	amount: number;
	so_amount: number;
	po_amount: number;
	to_amount: number;
}

export interface InfoLayerDiagnostic2ReportApi {
	layer_id: number;
	date: string;
	product_id: number;
	product_name: string;
	stock_id: number;
	stock_name: string;
	open_amount: number;
	items_amount: number;
	open_value: number;
	items_value: number;
	errors: ErrorDiagnostic2ReportApi[];
}

export interface InfoErrorDiagnostic2ReportApi {
	layer_id: number;
	product_id: number;
	stock_id: number;
	errors: ErrorDiagnostic2ReportApi[];
}

export interface InfoDiagnostic2ReportApi {
	amount_and_values: InfoAmountAndValueDiagnostic2ReportApi[];
	document_amount_and_values: DocumentAmountDiagnostic2ReportApi[];
	layers: InfoLayerDiagnostic2ReportApi[];
	errors: InfoErrorDiagnostic2ReportApi[];
}

export enum BundleStatus {
	DRAFT = "DRAFT",
	OPEN = "OPEN",
	CLOSED = "CLOSED",
	VOID = "VOID",
}

export interface BundleApi {
	id: number;
	bundled_at: string;
	number: number;
	status: BundleStatus;
	source_stock: StockApi;
	destination_stock: StockApi;
	tags: TagApi[];
	file_attachments: FileAttachmentApi[];
	reference_number?: string;
	items: BundleItemApi[];
	custom_fields: CustomFieldEntityApi[];
	source_stock_id: number;
	destination_stock_id: number;
	created_at: string;
	comment?: string;
	total_fifo_cost_net_money: MoneyApi;
	created_by?: string;
	quantity_produced: number | null;
	contact?: ContactApi;
	contact_id?: number;
	document_number: string;
	total_mac_cost_net_money: MoneyApi;
}

export interface BundleItemApi {
	id?: number;
	position: number;
	product: ProductApi | null;
	product_id: number | null;
	volume: number | null;
	quantity: number | null;
	unit_fifo_cost_net_money: MoneyApi | null;
	total_fifo_cost_net_money: MoneyApi | null;
	items: BundleItemItemApi[];
	quantity_produced: number | null;
	comment?: string;
}

export interface BundleItemItemApi {
	id?: number;
	quantity: number | null;
	product_name: string | null;
	product_id: number | null;
	unit_fifo_cost_net_money: MoneyApi | null;
	total_fifo_cost_net_money: MoneyApi | null;
	state: StockProductStateApi | null;
	measure_type: MeasureType | null;
	amount: number | null;
	product?: ProductApi;
}

export type ArchivesStatus = "CLOSED" | "CLOSING" | "OPEN" | "IN_PROGRESS";

export interface ArchivesApi {
	id: number;
	status: ArchivesStatus;
	date: string;
}

export interface InvoiceApi {
	id: number;
	status: InvoiceStatus;
	dates: InvoiceDatesApi;
	recipient: InvoiceRecipientApi | null;
	receiver?: InvoiceReceiverApi;
	receiver_id?: number;
	recipient_id: number;
	orders: InvoiceOrderApi[];
	summary: InvoiceSummaryApi;
	document_number: InvoiceDocumentNumberApi;
	calculation_type: InvoiceCalculationType;
	place_of_issue?: string;
	issuer_person_name?: string;
	notes?: string;
	custom_fields: CustomFieldEntityApi[];
	items: InvoiceItemApi[];
	payments: InvoicePaymentApi[];
	copied?: boolean;
	paid: boolean;
	issuer?: InvoiceIssuerApi;
}

export interface InvoiceIssuerApi {
	address?: AddressApi;
	contact?: ContactDetailsApi;
	name: string;
	tax_id_no: string;
	bank_account?: BankAccountApi;
}

export enum InvoiceCalculationType {
	"NET" = "NET",
	"GROSS" = "GROSS",
}

export interface InvoicePaymentApi {
	id: number;
	paid: boolean;
	payment_type: InvoicePaymentType;
	price_paid: MoneyApi;
	payment_date: string;
}

export enum InvoicePaymentType {
	BANK_TRANSFER = "BANK_TRANSFER",
	CASH = "CASH",
	CREDIT_CARD = "CREDIT_CARD",
	ONLINE = "ONLINE",
	COMPENSATION = "COMPENSATION",
	BARTER = "BARTER",
	VOUCHER = "VOUCHER",
}

export interface InvoiceItemApi {
	id: number | null;
	external_id?: string;
	measure_type: MeasureType | null;
	name: string | null;
	pkwiu: string | null;
	price: MoneyApi | null;
	price_gross: MoneyApi | null;
	price_net: MoneyApi | null;
	price_sum_net: MoneyApi | null;
	price_sum_gross: MoneyApi | null;
	tax: TaxApi | null;
	tax_id: number | null;
	volume: number | null;
	product_id: number | null;
	quantity: number | null;
	gtu: string | null;
}

export interface InvoiceDocumentNumberApi {
	type_name: "INVOICE";
	full_number: string;
}

export interface InvoiceRecipientApi {
	id: number;
	name: string;
	tax_id_no: string;
	address?: AddressApi;
	contact?: ContactDetailsApi;
}

export type InvoiceReceiverApi = InvoiceRecipientApi;

export interface InvoiceDatesApi {
	issue_date: string;
	sale_date: string;
	pay_date: string;
}

export interface InvoiceOrderApi {
	id: number | null;
	order_id: number;
	order_number: string;
	order_sale_date: string;
	order_tax_id_no: string;
	order_external_number?: string;
}

export interface InvoiceSummaryApi {
	price_sum_net: MoneyApi;
	price_sum_gross: MoneyApi;
	price_sum_tax: MoneyApi;
	price_to_pay: MoneyApi;
	invoice_taxes: InvoiceSummaryTaxApi[];
	price_paid: MoneyApi;
}

export interface InvoiceSummaryTaxApi {
	tax: { id: number; amount: number };
	price_net: MoneyApi;
	price_gross: MoneyApi;
	price_tax: MoneyApi;
}

export type InvoiceStatus = "DRAFT" | "CLOSED";

export interface ProductContactApi {
	contact: { name: string; id: number };
	reference_price: MoneyApi;
}

//CORRECTION

export interface CorrectionFullApi {
	date_from: string;
	date_to: string;
	product_ids: number;
	since_last_archive: boolean;
}

export interface ProductGroupsApi {
	id: number;
	name: string;
	recipe_amount: number;
	measure_type: MeasureType;
	category_name: string;
	product_cost_price_money: MoneyApi;
	product_cost_price_gross_money: MoneyApi;
	reference_price_money: MoneyApi;
	reference_price_gross_money: MoneyApi;
	last_price_money: MoneyApi;
	last_price_gross_money: MoneyApi;
	components: ProductGroupsApi[];
}

export enum NotClosedOrder {
	TRANSFER_ORDER = "TRANSFER_ORDER",
	PURCHASE_CORRECTION = "PURCHASE_CORRECTION",
	PURCHASE_ORDER = "PURCHASE_ORDER",
	SALES_ORDER = "SALES_ORDER",
	STOCK_ADJUSTMENT = "STOCK_ADJUSTMENT",
	BUNDLE = "BUNDLE",
}
